import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Reusable manual slideshow used by case-study cards and bento tiles.
 *
 * Props:
 *   media: Array<{ type: 'image' | 'video', src: string }>
 *   alt: optional alt text base
 *   objectFit: 'cover' (default) | 'contain'
 *   rounded: tailwind class for rounding (default 'rounded-md')
 *
 * The component:
 *   - cross-fades between slides
 *   - shows position dots top-left
 *   - exposes left / right chevron buttons (visible on parent hover via group-hover)
 *   - calls preventDefault/stopPropagation on its buttons so wrapping <a> tags
 *     don't navigate when navigating slides
 */
export default function MediaSlideshow({
  media,
  alt = '',
  objectFit = 'cover',
  rounded = 'rounded-md',
}) {
  const [index, setIndex] = useState(0);
  const videoRef = useRef(null);

  const goTo = (next) => {
    setIndex((next + media.length) % media.length);
  };

  useEffect(() => {
    const current = media[index];
    if (current?.type === 'video') {
      const v = videoRef.current;
      if (v) {
        v.currentTime = 0;
        v.play().catch(() => {});
      }
    }
  }, [index, media]);

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const fitClass = objectFit === 'contain' ? 'object-contain' : 'object-cover';

  return (
    <div className={`absolute inset-0 ${rounded} overflow-hidden`}>
      {media.map((item, i) => {
        const active = i === index;
        const common = `absolute inset-0 w-full h-full ${fitClass} transition-opacity duration-500 ${
          active ? 'opacity-100' : 'opacity-0'
        }`;
        if (item.type === 'video') {
          return (
            <video
              key={`v-${item.src || i}`}
              ref={active ? videoRef : null}
              src={item.src}
              className={common}
              muted
              playsInline
              preload="metadata"
              loop
              aria-hidden={!active}
            />
          );
        }
        return (
          <img
            key={`img-${item.src || i}`}
            src={item.src}
            alt={active ? alt : ''}
            className={common}
            loading={i === 0 ? 'eager' : 'lazy'}
            aria-hidden={!active}
          />
        );
      })}

      {media.length > 1 && (
        <div className="absolute top-5 left-5 flex items-center gap-1.5 z-20 pointer-events-none">
          {media.map((item, i) => (
            <span
              key={`dot-${item.src || i}`}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === index ? 'w-6 bg-[#f4efe6]' : 'w-2 bg-white/40'
              }`}
            />
          ))}
        </div>
      )}

      {media.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            onClick={(e) => {
              stop(e);
              goTo(index - 1);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/55 backdrop-blur-sm border border-white/20 text-[#f4efe6] flex items-center justify-center hover:bg-black/80 hover:border-[#00b8ff] transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            onClick={(e) => {
              stop(e);
              goTo(index + 1);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/55 backdrop-blur-sm border border-white/20 text-[#f4efe6] flex items-center justify-center hover:bg-black/80 hover:border-[#00b8ff] transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
}
